/**
 * Status Grid Widget Component
 * Displays multiple items as cards with status indicators
 */

import { StatusIndicator } from './StatusIndicator';
import type { StatusGridItem, StatusField } from '../../types';
import clsx from 'clsx';

export interface StatusGridWidgetProps {
  items?: StatusGridItem[];
  config?: {
    titleField?: string;
    subtitleField?: string;
    statusFields?: StatusField[];
    cardStyle?: 'compact' | 'detailed';
    columnsPerRow?: number;
  };
  loading?: boolean;
  onCardClick?: (item: StatusGridItem) => void;
}

export function StatusGridWidget({
  items = [],
  config = {},
  loading,
  onCardClick,
}: StatusGridWidgetProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border p-4 animate-pulse">
            <div className="h-5 w-24 bg-gray-200 rounded mb-2" />
            <div className="h-4 w-16 bg-gray-200 rounded mb-3" />
            <div className="flex gap-2">
              <div className="h-6 w-16 bg-gray-200 rounded-full" />
              <div className="h-6 w-16 bg-gray-200 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!items || items.length === 0) {
    return <StatusGridEmpty />;
  }

  const columnsPerRow = config.columnsPerRow || 4;
  const cardStyle = config.cardStyle || 'compact';

  const gridCols: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
    5: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
    6: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
  };

  return (
    <div className={clsx('grid gap-4 p-4', gridCols[columnsPerRow] || 'grid-cols-4')}>
      {items.map((item, idx) => (
        <StatusCardComponent
          key={idx}
          item={item}
          style={cardStyle}
          onClick={onCardClick ? () => onCardClick(item) : undefined}
        />
      ))}
    </div>
  );
}

interface StatusCardComponentProps {
  item: StatusGridItem;
  style: 'compact' | 'detailed';
  onClick?: () => void;
}

function StatusCardComponent({ item, style, onClick }: StatusCardComponentProps) {
  return (
    <div
      className={clsx(
        'bg-white rounded-lg shadow-sm border border-gray-200 p-4 transition-all',
        onClick && 'cursor-pointer hover:shadow-md hover:border-blue-300'
      )}
      onClick={onClick}
    >
      {/* Title */}
      <div className="font-semibold text-lg text-gray-900 truncate" title={item.title}>
        {item.title || '—'}
      </div>

      {/* Subtitle (detailed mode only) */}
      {style === 'detailed' && item.subtitle && (
        <div className="text-gray-500 text-sm truncate mt-0.5" title={item.subtitle}>
          {item.subtitle}
        </div>
      )}

      {/* Status Indicators */}
      <div className="flex flex-wrap gap-2 mt-3">
        {item.statuses.map((status, sIdx) => (
          <StatusIndicator
            key={sIdx}
            label={status.label}
            value={status.value}
            color={status.color || '#9ca3af'}
            icon={status.icon || 'minus'}
            size="sm"
          />
        ))}
      </div>

      {/* Additional Info (detailed mode) */}
      {style === 'detailed' && item.raw && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
            {Object.entries(item.raw)
              .filter(([key]) => !key.startsWith('@') && !['Order', 'Item', 'OrderStatus'].includes(key))
              .slice(0, 4)
              .map(([key, value]) => (
                <div key={key} className="truncate">
                  <span className="font-medium">{key}:</span> {String(value)}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Empty state component
export function StatusGridEmpty() {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-gray-500">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      </div>
      <p className="text-lg font-medium">No data available</p>
      <p className="text-sm">Configure an endpoint to display data here</p>
    </div>
  );
}
